import React from "react"
import {Wrench, Calendar, Droplet, AlertCircle, CheckCircle, Snowflake} from 'lucide-react'
import { motion } from "framer-motion"

const MaintenancePage = () => {
  return (
    <div className="bg-zinc-900 min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-zinc-950 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Wrench className="text-orange-500 mx-auto mb-6" size={64} />
            <h1 className="text-5xl font-bold text-white mb-6">
              Maintenance Best Practices for <span className="text-orange-500">Shed Moving Equipment</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Comprehensive maintenance guidelines to maximize equipment lifespan and ensure safe operation. Learn about preventive care schedules and common troubleshooting procedures.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Maintenance Schedule */}
      <section className="py-20 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Calendar className="text-orange-500 mx-auto mb-4" size={48} />
            <h2 className="text-4xl font-bold text-white mb-4">
              Preventive Maintenance Schedule
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Regular maintenance intervals to keep equipment operating at peak performance
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-zinc-950 p-8 rounded-lg border border-zinc-800"
            >
              <h3 className="text-2xl font-bold text-white mb-6 text-center">Daily Checks</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-white font-semibold">Visual Inspection</p>
                    <p className="text-gray-400 text-sm">Check for leaks, cracks, or loose parts</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-white font-semibold">Hydraulic Fluid Level</p>
                    <p className="text-gray-400 text-sm">Verify fluid is at proper level</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-white font-semibold">Tire Pressure</p>
                    <p className="text-gray-400 text-sm">Check all tires meet specifications</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-white font-semibold">Control Operation</p>
                    <p className="text-gray-400 text-sm">Test all hydraulic controls</p>
                  </div>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-zinc-950 p-8 rounded-lg border border-zinc-800"
            >
              <h3 className="text-2xl font-bold text-white mb-6 text-center">Monthly Service</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-white font-semibold">Lubrication Points</p>
                    <p className="text-gray-400 text-sm">Grease all pivot points and bearings</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-white font-semibold">Bolt Torque Check</p>
                    <p className="text-gray-400 text-sm">Verify critical fasteners are tight</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-white font-semibold">Hose Inspection</p>
                    <p className="text-gray-400 text-sm">Check for wear, cracks, or damage</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-white font-semibold">Safety Features Test</p>
                    <p className="text-gray-400 text-sm">Verify all safety systems function</p>
                  </div>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-zinc-950 p-8 rounded-lg border border-zinc-800"
            >
              <h3 className="text-2xl font-bold text-white mb-6 text-center">Annual Overhaul</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-white font-semibold">Hydraulic Fluid Change</p>
                    <p className="text-gray-400 text-sm">Replace with manufacturer-specified fluid</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-white font-semibold">Filter Replacement</p>
                    <p className="text-gray-400 text-sm">Change all hydraulic filters</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-white font-semibold">Structural Inspection</p>
                    <p className="text-gray-400 text-sm">Professional examination of frame</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-orange-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-white font-semibold">Load Testing</p>
                    <p className="text-gray-400 text-sm">Verify rated capacity performance</p>
                  </div>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Hydraulic System Care */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <Droplet className="text-orange-500 mb-6" size={48} />
              <h2 className="text-4xl font-bold text-white mb-6">
                Hydraulic System Maintenance
              </h2>
              <p className="text-gray-300 mb-8 text-lg leading-relaxed">
                The hydraulic system is critical to equipment operation. Proper fluid management and component care prevent costly failures and downtime.
              </p>

              <div className="space-y-6">
                <div className="bg-zinc-900 p-6 rounded-lg border border-zinc-800">
                  <h3 className="text-xl font-bold text-white mb-3">Fluid Quality</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li>• Use only manufacturer-recommended hydraulic fluid</li>
                    <li>• Check fluid clarity - cloudy fluid indicates contamination</li>
                    <li>• Monitor fluid temperature during operation</li>
                    <li>• Replace fluid if burned smell or darkening occurs</li>
                  </ul>
                </div>

                <div className="bg-zinc-900 p-6 rounded-lg border border-zinc-800">
                  <h3 className="text-xl font-bold text-white mb-3">Pressure Management</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li>• Never exceed maximum pressure specifications</li>
                    <li>• Install and monitor pressure gauges regularly</li>
                    <li>• Relief valves must be tested monthly</li>
                    <li>• Address pressure spikes immediately</li>
                  </ul>
                </div>

                <div className="bg-zinc-900 p-6 rounded-lg border border-zinc-800">
                  <h3 className="text-xl font-bold text-white mb-3">Contamination Prevention</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li>• Keep reservoir caps sealed when not servicing</li>
                    <li>• Clean fill area before adding fluid</li>
                    <li>• Replace filters per manufacturer schedule</li>
                    <li>• Store unused fluid in sealed containers</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <AlertCircle className="text-orange-500 mb-6" size={48} />
              <h2 className="text-4xl font-bold text-white mb-6">
                Common Issues & Troubleshooting
              </h2>

              <div className="space-y-6">
                <div className="bg-zinc-900 p-6 rounded-lg border-l-4 border-red-500">
                  <h3 className="text-xl font-bold text-white mb-2">Sluggish Hydraulic Response</h3>
                  <p className="text-gray-400 mb-3">Possible Causes:</p>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• Low fluid level - check reservoir</li>
                    <li>• Air in system - bleed hydraulic lines</li>
                    <li>• Clogged filter - replace hydraulic filter</li>
                    <li>• Cold fluid - allow warm-up period</li>
                  </ul>
                </div>

                <div className="bg-zinc-900 p-6 rounded-lg border-l-4 border-yellow-500">
                  <h3 className="text-xl font-bold text-white mb-2">Excessive Noise</h3>
                  <p className="text-gray-400 mb-3">Possible Causes:</p>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• Pump cavitation - check fluid level and inlet</li>
                    <li>• Loose mounting bolts - torque to specification</li>
                    <li>• Worn pump - professional inspection required</li>
                    <li>• Air in lines - bleed system properly</li>
                  </ul>
                </div>

                <div className="bg-zinc-900 p-6 rounded-lg border-l-4 border-orange-500">
                  <h3 className="text-xl font-bold text-white mb-2">Fluid Leaks</h3>
                  <p className="text-gray-400 mb-3">Possible Causes:</p>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• Worn seals - replace damaged seals</li>
                    <li>• Loose fittings - tighten to proper torque</li>
                    <li>• Damaged hoses - replace immediately</li>
                    <li>• Overpressure - check relief valve setting</li>
                  </ul>
                </div>

                <div className="bg-zinc-900 p-6 rounded-lg border-l-4 border-blue-500">
                  <h3 className="text-xl font-bold text-white mb-2">Overheating</h3>
                  <p className="text-gray-400 mb-3">Possible Causes:</p>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• Low fluid level - add proper hydraulic oil</li>
                    <li>• Excessive load - reduce operating pressure</li>
                    <li>• Dirty cooler - clean heat exchanger fins</li>
                    <li>• Continuous operation - allow cool-down periods</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Storage and Winterization */}
      <section className="py-20 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Snowflake className="text-orange-500 mx-auto mb-4" size={48} />
            <h2 className="text-4xl font-bold text-white mb-4">
              Storage & Winterization
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Proper storage procedures protect equipment during off-season periods
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-zinc-950 p-8 rounded-lg border border-zinc-800"
            >
              <h3 className="text-2xl font-bold text-white mb-6">Pre-Storage Preparation</h3>
              <ol className="space-y-4 text-gray-300 list-decimal ml-5">
                <li>
                  <strong className="text-white">Clean Thoroughly</strong>
                  <p className="text-sm mt-1">Remove dirt, mud, and debris from all surfaces</p>
                </li>
                <li>
                  <strong className="text-white">Inspect and Repair</strong>
                  <p className="text-sm mt-1">Address any damage or maintenance issues before storage</p>
                </li>
                <li>
                  <strong className="text-white">Lubricate All Points</strong>
                  <p className="text-sm mt-1">Grease all fittings to prevent corrosion</p>
                </li>
                <li>
                  <strong className="text-white">Protect Hydraulics</strong>
                  <p className="text-sm mt-1">Cycle all cylinders with fresh fluid coating</p>
                </li>
                <li>
                  <strong className="text-white">Tire Care</strong>
                  <p className="text-sm mt-1">Inflate to maximum pressure, place on blocks if possible</p>
                </li>
                <li>
                  <strong className="text-white">Cover Equipment</strong>
                  <p className="text-sm mt-1">Use breathable cover to protect from elements</p>
                </li>
              </ol>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-zinc-950 p-8 rounded-lg border border-zinc-800"
            >
              <h3 className="text-2xl font-bold text-white mb-6">Winter-Specific Considerations</h3>
              <div className="space-y-4">
                <div className="bg-zinc-900 p-4 rounded-lg">
                  <h4 className="text-white font-bold mb-2">Indoor Storage (Preferred)</h4>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• Store in dry, temperature-controlled environment</li>
                    <li>• Prevents freeze damage to hydraulic system</li>
                    <li>• Reduces corrosion and weathering</li>
                  </ul>
                </div>

                <div className="bg-zinc-900 p-4 rounded-lg">
                  <h4 className="text-white font-bold mb-2">Outdoor Storage</h4>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• Use low-temperature hydraulic fluid</li>
                    <li>• Drain water from any reservoirs</li>
                    <li>• Ensure proper drainage under equipment</li>
                    <li>• Check cover regularly for water pooling</li>
                  </ul>
                </div>

                <div className="bg-zinc-900 p-4 rounded-lg">
                  <h4 className="text-white font-bold mb-2">Monthly Checks During Storage</h4>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• Inspect for rodent damage to hoses/wiring</li>
                    <li>• Check tire pressure and adjust as needed</li>
                    <li>• Verify cover is secure and dry</li>
                    <li>• Exercise hydraulics briefly if possible</li>
                  </ul>
                </div>

                <div className="bg-zinc-900 p-4 rounded-lg">
                  <h4 className="text-white font-bold mb-2">Spring Recommissioning</h4>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• Complete full inspection before use</li>
                    <li>• Check all fluid levels and quality</li>
                    <li>• Test all systems with no load first</li>
                    <li>• Perform light-duty cycles before full operation</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Maintenance Record Keeping */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-8 text-center">
            Maintenance Record Keeping
          </h2>
          <div className="bg-zinc-900 p-8 rounded-lg border border-zinc-800">
            <p className="text-gray-300 mb-6 text-lg">
              Detailed maintenance logs help track equipment history, identify patterns, and maintain warranty coverage. Proper documentation is essential for safe operation and resale value.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-bold text-white mb-4">What to Document</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• Date and hours of operation for each use</li>
                  <li>• All maintenance performed with details</li>
                  <li>• Parts replaced with part numbers</li>
                  <li>• Fluid changes and filter replacements</li>
                  <li>• Any repairs or adjustments made</li>
                  <li>• Operator notes on performance issues</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-4">Benefits of Good Records</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• Warranty claim documentation</li>
                  <li>• Troubleshooting history reference</li>
                  <li>• Resale value verification</li>
                  <li>• Safety compliance proof</li>
                  <li>• Predictive maintenance scheduling</li>
                  <li>• Operating cost tracking</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-16 bg-zinc-900 border-t border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-zinc-950 p-8 rounded-lg border-2 border-orange-500/30">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <AlertCircle className="text-orange-500" size={24} />
              Important Maintenance Disclaimer
            </h3>
            <p className="text-gray-300 leading-relaxed">
              This information is for educational purposes only. Always follow manufacturer-specific maintenance procedures and intervals. Improper maintenance can void warranties and create safety hazards. Consult qualified technicians for major repairs or if you are unsure about any procedure. MuleShed.com is not responsible for equipment damage or injury resulting from maintenance performed based on this information.
            </p>
          </div>
        </div>
      </section>
    </div>
  )
}

export default MaintenancePage